/**
 * The menu under your name.
 *
 * Things that are about you rather than about coaching: your profile, My
 * Portal (everything you've made or kept), the tour, and sending us content.
 * They used to be three more rows and two buttons at the bottom of the
 * sidebar, which is most of why the sidebar read as a list to wade through.
 *
 * Opens upward from the profile card on desktop; the phone drawer draws the
 * same rows inline (PROFILE_MENU_ROWS).
 */
import React, { useEffect, useRef } from "react";
import { UserRound, Briefcase, Compass, Plus } from "lucide-react";
import { GOLD } from "../constants/theme";

/** The rows, in order. The caller decides what each id does. */
export const PROFILE_MENU_ROWS = [
  { id: "profile", label: "My profile",     Icon: UserRound },
  { id: "portal",  label: "My Portal",      Icon: Briefcase },
  { id: "tour",    label: "What's here",    Icon: Compass },
  { id: "submit",  label: "Submit Content", Icon: Plus, accent: true },
];

export function ProfileMenu({ open, onClose, onPick, nav }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    // The card that opens the menu toggles it itself; closing here as well
    // would close and reopen it in one tap.
    const onDown = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest("[data-profile-anchor]")) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div ref={ref} role="menu" aria-label="Your menu" className="bam-menu-in from-b"
      style={{ position: "absolute", left: 10, right: 10, bottom: "calc(100% + 6px)", zIndex: 30,
        background: "#121212", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12,
        boxShadow: "0 18px 50px rgba(0,0,0,0.6)", padding: 6 }}>
      {PROFILE_MENU_ROWS.map(r => {
        const active = nav === r.id;
        return (
          <button key={r.id} type="button" role="menuitem" className="btn"
            onClick={() => { onClose(); onPick(r.id); }}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, minHeight: 40,
              padding: "8px 10px", borderRadius: 8, border: "none", cursor: "pointer", textAlign: "left",
              background: active ? "rgba(226,221,159,0.08)" : "transparent",
              color: r.accent ? GOLD : (active ? "#F5F5F5" : "#B5B2A8"),
              fontFamily: "inherit", fontSize: 13, fontWeight: 600 }}>
            <r.Icon size={15} color={r.accent || active ? GOLD : "#777"}/>
            {r.label}
          </button>
        );
      })}
    </div>
  );
}

export default ProfileMenu;
